'use client'

import { Suspense } from 'react'

import { useSuspenseQueries } from '@tanstack/react-query'
import { ChevronDown, ChevronRight } from 'lucide-react'
import Link from 'next/link'
import { twMerge } from 'tailwind-merge'

import PopoverV2 from '@/components/common/PopoverV2'
import { Skeleton } from '@/components/common/Skeleton'

import type { RecipientHierarchyNode } from '@/context/usaspending'

import { useRecipientSectionQueries } from '@/hooks/usaspending'

import { QueryErrorBoundary } from '../DataErrorBoundary'
import { Currency } from '../Formatters'
import { useRecipientDetail } from './context'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Max children shown inline in parent view before popover */
const VISIBLE_CHILDREN = 3

// ---------------------------------------------------------------------------
// Chip variant config
// ---------------------------------------------------------------------------

type ChipVariant = 'parent' | 'child'

const CHIP_STYLES: Record<ChipVariant, string> = {
  parent:
    'border border-purple-300 bg-purple-50 text-purple-800 hover:bg-purple-100 dark:border-purple-600 dark:bg-purple-900/30 dark:text-purple-200 dark:hover:bg-purple-900/50',
  child:
    'border border-primary-200 bg-white text-primary-700 hover:bg-primary-50 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-300 dark:hover:bg-primary-750',
}

const CHIP_BASE =
  'inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[11px] font-medium leading-tight transition-colors max-w-48 sm:max-w-64'

/** Non-interactive variant (no hover state) for chips without a link */
const CHIP_STYLES_STATIC: Record<ChipVariant, string> = {
  parent:
    'border border-purple-300 bg-purple-50 text-purple-800 dark:border-purple-600 dark:bg-purple-900/30 dark:text-purple-200',
  child:
    'border border-primary-200 bg-white text-primary-700 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-300',
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export function RecipientHierarchy() {
  const { recipientHash } = useRecipientDetail()

  return (
    <QueryErrorBoundary title="Failed to load hierarchy">
      <Suspense fallback={<HierarchySkeleton />}>
        <HierarchyContent recipientHash={recipientHash} />
      </Suspense>
    </QueryErrorBoundary>
  )
}

// ---------------------------------------------------------------------------
// Data loader
// ---------------------------------------------------------------------------

function HierarchyContent({ recipientHash }: { recipientHash: string }) {
  const queries = useRecipientSectionQueries()
  const [{ data: hierarchy }] = useSuspenseQueries({
    queries: [queries.hierarchy(recipientHash)],
  })

  if (!hierarchy.parent && hierarchy.children.length === 0) {
    return null
  }

  return (
    <div className="mt-3 border-t border-primary-100 pt-3 dark:border-primary-800">
      {hierarchy.parent ? (
        <ChildView
          parent={hierarchy.parent}
          siblings={hierarchy.siblings}
          totalSiblings={hierarchy.totalSiblings}
        />
      ) : (
        <ParentView
          selfNode={hierarchy.selfNode}
          children={hierarchy.children}
          totalChildren={hierarchy.totalChildren}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Child view:  "Subsidiary of [Parent]  ·  N siblings  [View ▾]"
// ---------------------------------------------------------------------------

function ChildView({
  parent,
  siblings,
  totalSiblings,
}: {
  parent: RecipientHierarchyNode
  siblings: RecipientHierarchyNode[]
  totalSiblings: number
}) {
  return (
    <div className="flex items-center gap-2 min-w-0 flex-wrap">
      <span className="text-[11px] text-primary-400 dark:text-primary-500">
        Subsidiary of
      </span>
      <NodeChip node={parent} variant="parent" />
      {totalSiblings > 0 && (
        <>
          <span className="text-[11px] text-primary-300 dark:text-primary-600">
            ·
          </span>
          <PopoverV2>
            <PopoverV2.Trigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 text-[11px] font-medium text-primary-500 transition-colors hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-200"
              >
                {totalSiblings} {totalSiblings === 1 ? 'sibling' : 'siblings'}
                <ChevronDown className="h-2.5 w-2.5" />
              </button>
            </PopoverV2.Trigger>
            <PopoverV2.Content align="start" className="w-[420px] p-0">
              <EntityList
                items={siblings}
                totalCount={totalSiblings}
                label="Siblings"
                contextName={parent.name}
              />
            </PopoverV2.Content>
          </PopoverV2>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Parent view:  "N subsidiaries  [Child] [Child] [Child]  [+N more ▾]"
// ---------------------------------------------------------------------------

function ParentView({
  selfNode,
  children,
  totalChildren,
}: {
  selfNode: RecipientHierarchyNode
  children: RecipientHierarchyNode[]
  totalChildren: number
}) {
  const visible = children.slice(0, VISIBLE_CHILDREN)
  const hiddenCount = Math.max(0, totalChildren - VISIBLE_CHILDREN)

  return (
    <div className="flex items-center gap-2 min-w-0 flex-wrap">
      <span className="text-[11px] font-medium text-primary-400 dark:text-primary-500">
        {totalChildren} {totalChildren === 1 ? 'subsidiary' : 'subsidiaries'}
      </span>
      {visible.map((child) => (
        <NodeChip key={child.recipientHash} node={child} variant="child" />
      ))}
      {hiddenCount > 0 && (
        <PopoverV2>
          <PopoverV2.Trigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-full border border-primary-200 bg-primary-50 px-2 py-0.5 text-[10px] font-medium text-primary-500 transition-colors hover:bg-primary-100 dark:border-primary-700 dark:bg-primary-800 dark:text-primary-400 dark:hover:bg-primary-700"
            >
              +{hiddenCount} more
              <ChevronDown className="h-2.5 w-2.5" />
            </button>
          </PopoverV2.Trigger>
          <PopoverV2.Content align="start" className="w-[420px] p-0">
            <EntityList
              items={children}
              totalCount={totalChildren}
              label="Subsidiaries"
              contextName={selfNode.name}
            />
          </PopoverV2.Content>
        </PopoverV2>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Popover entity list (shared by both views)
// ---------------------------------------------------------------------------

function EntityList({
  items,
  totalCount,
  label,
  contextName,
}: {
  items: RecipientHierarchyNode[]
  totalCount: number
  label: string
  contextName: string | null
}) {
  return (
    <div>
      <div className="border-b border-primary-100 px-3 py-2 dark:border-primary-800">
        <span className="text-xs font-semibold text-primary-700 dark:text-primary-300">
          {totalCount} {label}
        </span>
        {contextName && (
          <span className="text-xs text-primary-400 dark:text-primary-500">
            {' '}
            of {contextName}
          </span>
        )}
      </div>
      <div className="max-h-80 overflow-y-auto">
        {items.map((node) => (
          <EntityRow key={node.recipientHash} node={node} />
        ))}
      </div>
    </div>
  )
}

function EntityRow({ node }: { node: RecipientHierarchyNode }) {
  const location = formatLocation(node.city, node.state)
  const hasAmount = node.last12Months != null && node.last12Months > 0

  const content = (
    <div className="flex items-center gap-2 px-3 py-1.5 text-xs">
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-primary-800 dark:text-primary-200">
          {node.name || 'Unknown'}
        </div>
        {location && (
          <div className="truncate text-[11px] text-primary-400 dark:text-primary-500">
            {location}
          </div>
        )}
      </div>
      {hasAmount && (
        <Currency
          value={node.last12Months!}
          compact
          className="shrink-0 tabular-nums text-primary-500 dark:text-primary-400"
        />
      )}
      {node.recipientId && (
        <ChevronRight className="h-3 w-3 shrink-0 text-primary-300 dark:text-primary-600" />
      )}
    </div>
  )

  if (!node.recipientId) {
    return (
      <div className="border-b border-primary-50 last:border-b-0 dark:border-primary-800/50">
        {content}
      </div>
    )
  }

  return (
    <Link
      href={`/recipients/usaspending/${node.recipientId}`}
      prefetch={false}
      className="block border-b border-primary-50 transition-colors hover:bg-primary-50 last:border-b-0 dark:border-primary-800/50 dark:hover:bg-primary-800/50"
    >
      {content}
    </Link>
  )
}

// ---------------------------------------------------------------------------
// Single node chip
// ---------------------------------------------------------------------------

function NodeChip({
  node,
  variant,
}: {
  node: RecipientHierarchyNode
  variant: ChipVariant
}) {
  const chipContent = (
    <>
      <span className="truncate">{node.name || 'Unknown'}</span>
      {node.state && (
        <span className="shrink-0 text-[10px] font-normal opacity-50">
          {node.state}
        </span>
      )}
    </>
  )

  if (!node.recipientId) {
    return (
      <span
        className={twMerge(CHIP_BASE, CHIP_STYLES_STATIC[variant])}
        title={node.name || 'Unknown entity'}
      >
        {chipContent}
      </span>
    )
  }

  return (
    <Link
      href={`/recipients/usaspending/${node.recipientId}`}
      prefetch={false}
      className={twMerge(CHIP_BASE, CHIP_STYLES[variant])}
      title={node.name || 'Recipient'}
    >
      {chipContent}
    </Link>
  )
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatLocation(
  city: string | null | undefined,
  state: string | null | undefined,
): string | null {
  if (city && state) return `${city}, ${state}`
  return city || state || null
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function HierarchySkeleton() {
  return (
    <div className="mt-3 border-t border-primary-100 pt-3 dark:border-primary-800">
      <div className="flex items-center gap-2">
        <Skeleton className="h-5 w-24 rounded" />
        <Skeleton className="h-7 w-36 rounded-md" />
        <Skeleton className="h-7 w-28 rounded-md" />
        <Skeleton className="h-7 w-32 rounded-md" />
      </div>
    </div>
  )
}
